import * as React from "react";
import { cn } from "@/lib/utils";
import { Input } from "./input";
import { Button } from "./button";
import { Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { useAiCopySuggestion, type FieldContext } from "@/hooks/useAiCopySuggestion";

export interface AiInputProps extends React.ComponentProps<"input"> {
  fieldContext?: FieldContext;
  onSuggestionAccepted?: (suggestion: string) => void;
}

const AiInput = React.forwardRef<HTMLInputElement, AiInputProps>(
  ({ className, fieldContext, onSuggestionAccepted, value, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const { suggestions, isLoading, isOpen, generateSuggestions, dismiss } = useAiCopySuggestion();
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [keepButtonVisible, setKeepButtonVisible] = React.useState(false);

    React.useEffect(() => {
      setIsPopoverOpen(isOpen);
    }, [isOpen]);

    const handleAiClick = async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (!fieldContext) return;

      // Keep button visible even if input loses focus
      setKeepButtonVisible(true);
      setIsPopoverOpen(true);

      await generateSuggestions({
        ...fieldContext,
        currentValue: value?.toString() || '',
      });
    };

    const handleSuggestionClick = (suggestion: string) => {
      // Apply to parent-controlled state
      if (onSuggestionAccepted) {
        onSuggestionAccepted(suggestion);
      }
      // Also notify any onChange handlers to keep forms in sync
      if (props.onChange) {
        try {
          props.onChange({ target: { value: suggestion } } as any);
        } catch {}
      }
      // Small delay to ensure state updates before closing
      setTimeout(() => {
        dismiss();
        setIsPopoverOpen(false);
        setKeepButtonVisible(false);
      }, 50);
    };

    const handlePopoverClose = () => {
      setIsPopoverOpen(false);
      setKeepButtonVisible(false);
      dismiss();
    };

    return (
      <div className="relative w-full">
        <Input
          ref={ref}
          value={value}
          className={cn(fieldContext && "pr-10", className)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => {
            if (!keepButtonVisible) {
              setIsFocused(false);
            }
          }, 200)}
          {...props}
        />
        
        {fieldContext && (isFocused || keepButtonVisible) && (
          <Popover open={isPopoverOpen} onOpenChange={(open) => {
            setIsPopoverOpen(open);
            if (!open) {
              setKeepButtonVisible(false);
              dismiss();
            }
          }}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  "absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-primary hover:text-primary hover:bg-primary/10 transition-all duration-200",
                  (isFocused || keepButtonVisible) ? "opacity-100" : "opacity-0"
                )}
                onClick={handleAiClick}
                disabled={isLoading}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Sparkles className={cn("h-4 w-4", isLoading && "animate-pulse")} />
              </Button>
            </PopoverTrigger>
            <PopoverContent 
              className="w-80 p-0 z-[100] bg-white dark:bg-gray-800 pointer-events-auto" 
              align="end"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-sm">AI Suggestions</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePopoverClose}
                    className="h-6 text-xs"
                  >
                    Dismiss
                  </Button>
                </div>

                {isLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-16 bg-muted animate-pulse rounded-md"
                      />
                    ))}
                  </div>
                ) : suggestions.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSuggestionClick(suggestion);
                        }}
                        className="w-full text-left p-3 text-sm rounded-md border border-border hover:bg-accent hover:border-primary transition-all duration-200 cursor-pointer"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    Click the sparkle icon to generate suggestions
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    );
  }
);

AiInput.displayName = "AiInput";

export { AiInput };
